import {Button, Card, Container, Divider, SimpleGrid, Stack, Text, Title} from '@mantine/core';
import {useNavigate} from 'react-router-dom';
import {
    AiOutlineCloseCircle,
    AiOutlineCreditCard,
    AiOutlineDollarCircle,
    AiOutlineFileText,
    AiOutlineLock,
    AiOutlineSwap,
    AiOutlineSync,
    AiOutlineWallet,
} from 'react-icons/ai';

const coreFlows = [
    {
        title: 'Save Payment Method',
        description: 'Create a setup intent and save a payment method to a customer using Stripe.js PaymentElement.',
        icon: <AiOutlineCreditCard size={32}/>,
        path: '/payment-flow-tester/save-payment-method',
    },
    {
        title: 'One-Time Payment',
        description: 'Create a payment intent and process a one-time charge using Stripe.js PaymentElement.',
        icon: <AiOutlineDollarCircle size={32}/>,
        path: '/payment-flow-tester/one-time-payment',
    },
    {
        title: 'Pay with Saved Method',
        description: 'Charge a previously saved payment method without collecting card details again.',
        icon: <AiOutlineWallet size={32}/>,
        path: '/payment-flow-tester/pay-with-saved-method',
    },
    {
        title: 'Subscription',
        description: 'Save a payment method via setup intent, then create a subscription with the saved payment method.',
        icon: <AiOutlineSync size={32}/>,
        path: '/payment-flow-tester/subscription',
    },
    {
        title: 'Cancel Subscription',
        description: 'Cancel an active subscription immediately or at the end of the billing period.',
        icon: <AiOutlineCloseCircle size={32}/>,
        path: '/payment-flow-tester/cancel-subscription',
        color: 'red',
    },
    {
        title: 'Update Subscription',
        description: 'Change the price/plan of an active subscription with automatic proration.',
        icon: <AiOutlineSwap size={32}/>,
        path: '/payment-flow-tester/update-subscription',
    },
    {
        title: 'Invoice Payment',
        description: 'Create an invoice, add a line item, and send it to the customer.',
        icon: <AiOutlineFileText size={32}/>,
        path: '/payment-flow-tester/invoice-payment',
    },
];

const edgeCaseFlows = [
    {
        title: 'Declined Card',
        description: 'Test error handling when a card is declined. Uses Stripe test card 4000 0000 0000 0002.',
        icon: <AiOutlineCloseCircle size={32}/>,
        path: '/payment-flow-tester/declined-card',
        color: 'orange',
    },
    {
        title: '3D Secure Authentication',
        description: 'Test the 3DS authentication challenge flow. Uses Stripe test card 4000 0025 0000 3155.',
        icon: <AiOutlineLock size={32}/>,
        path: '/payment-flow-tester/3d-secure',
        color: 'blue',
    },
];

export const IndexPage = () => {
    const navigate = useNavigate();

    const renderCards = (flows: typeof coreFlows) => (
        <SimpleGrid cols={{base: 1, sm: 2, md: 3}}>
            {flows.map(flow => (
                <Card key={flow.path} withBorder padding="lg" radius="md">
                    <Stack gap="md">
                        <Text c={flow.color || 'blue'}>{flow.icon}</Text>
                        <Title order={4}>{flow.title}</Title>
                        <Text size="sm" c="dimmed" style={{flex: 1}}>{flow.description}</Text>
                        <Button
                            variant="light"
                            color={flow.color}
                            mt="auto"
                            onClick={() => navigate(flow.path)}
                        >
                            Start Test
                        </Button>
                    </Stack>
                </Card>
            ))}
        </SimpleGrid>
    );

    return (
        <Container size="lg" py="xl">
            <Stack gap="xl">
                <div>
                    <Title order={2}>Payment Flow Tester</Title>
                    <Text c="dimmed" mt="xs">
                        Test Stripe payment workflows end-to-end. These flows simulate the same API calls and Stripe.js
                        interactions that consumer applications use.
                    </Text>
                </div>

                <div>
                    <Title order={4} mb="md">Core Flows</Title>
                    {renderCards(coreFlows)}
                </div>

                <Divider/>

                <div>
                    <Title order={4} mb="md">Edge Case Tests</Title>
                    {renderCards(edgeCaseFlows)}
                </div>
            </Stack>
        </Container>
    );
};
